import React, { useContext } from 'react';
import { StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { H1, H5, H6 } from '../text';
import Utils from '../../common/Utils';
import ComponentContainer from '../common/ComponentContainer';
import { HorizontalRule } from '../common/HorizontalRule';
import { StyledWebView } from '../common/StyledWebView';
import AppContext from '../../contexts/App';
import LayoutContext from '../../contexts/Layout';
import { fetchPage } from '../../common/Api';

const STYLED_WEB_VIEW_ID = 'jobsContainer';

export const JobsContainer = ({ bottomText, jobs, nodeTypeAlias = 'JobsContainer', title, width }) => {
    const { dispatch } = useContext(AppContext);
    const { layout } = useContext(LayoutContext);

    const renderContainerHeader = () => {
        if (!title) {
            return null;
        }

        return (
            <View style={{ alignItems: 'center', flexDirection: 'row', justifyContent: 'center' }}>
                <H1 text={title} />
            </View>
        );
    };

    const renderListings = () => {
        const groupedJobs = {};

        jobs.forEach(job => {
            const department = job.department;

            if (groupedJobs.hasOwnProperty(department)) {
                groupedJobs[department].push(job);
            } else {
                groupedJobs[department] = [job];
            }
        });

        return (
            <View style={{ marginBottom: layout.styleGuide.spacer2 }}>
                {Object.entries(groupedJobs).map(([department, jobs]) => {
                    return (
                        <View key={department} style={{ alignItems: 'center' }}>
                            <View style={{ flexDirection: 'row', width: '100%' }}>
                                <H6
                                    dataSet={{ spacerBottom: `StyleGuide.Spacer4(${layout.styleGuide.spacer4})` }}
                                    style={{ color: '#808080', marginBottom: layout.styleGuide.spacer4 }}
                                    text={department}
                                />
                            </View>
                            {jobs.map(job => {
                                if (job) {
                                    const rootPath = job?.rootPath;
                                    const jobTitle = job?.jobTitle;
                                    const organization = job?.organizationFilter;
                                    const organizations = organization?.join(', ');

                                    return (
                                        <View
                                            dataSet={{ spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})` }}
                                            key={`job-${job.id}`}
                                            style={{
                                                marginBottom: layout.styleGuide.spacer3,
                                                justifyContent: 'space-between',
                                                flexDirection: 'row',
                                                width: '100%'
                                            }}
                                        >
                                            <View style={{ width: '50%' }}>
                                                <H5
                                                    onPress={async () => await fetchPage(dispatch, Utils.cleanupInternalUrl(rootPath))}
                                                    style={{ color: '#153d6e' }}
                                                    text={jobTitle}
                                                />
                                            </View>
                                            <View style={{ width: '50%', alignItems: 'flex-end' }}>
                                                <H5 text={organizations} />
                                            </View>
                                        </View>
                                    );
                                }
                            })}
                        </View>
                    );
                })}
            </View>
        );
    };

    if (jobs?.length === 0) {
        return null;
    }

    return (
        <View dataSet={{ umbracoComponentName: nodeTypeAlias }}>
            {renderContainerHeader()}
            <ComponentContainer>
                {renderListings()}

                {!!bottomText && (
                    <View style={{ marginTop: layout.styleGuide.spacer2 }}>
                        <HorizontalRule />

                        <View style={StyleSheet.flatten(Styles.styledHtmlContainer, { marginVertical: layout.styleGuide.spacer3 })}>
                            <StyledWebView html={bottomText} nodeId={STYLED_WEB_VIEW_ID} width={width * 0.65} />
                        </View>
                    </View>
                )}
            </ComponentContainer>
        </View>
    );
};

const Styles = StyleSheet.create({
    styledHtmlContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        bottom: 10,
        padding: 20
    }
});

JobsContainer.propTypes = {
    bottomText: PropTypes.string,
    jobs: PropTypes.arrayOf(
        PropTypes.shape({
            ctaButtonText: PropTypes.string,
            ctaLink: PropTypes.shape({
                externalUrl: PropTypes.string,
                internalKey: PropTypes.string,
                isExternal: PropTypes.bool
            }),
            featuredImage: PropTypes.shape({
                cdnImagePath: PropTypes.string
            }),
            jobDescription: PropTypes.string,
            jobTitle: PropTypes.string,
            jobType: PropTypes.string,
            jobLocation: PropTypes.string,
            map: PropTypes.string,
            organizationFilter: PropTypes.arrayOf(PropTypes.string),
            responsibilities: PropTypes.string,
            qualifications: PropTypes.string
        })
    ),
    organizationFilter: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string
};

export default JobsContainer;
